import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ApolloError } from '@apollo/client';
import { Box, ButtonV2, CheckboxV2, TextV2, TooltipV2 } from '@withjoy/joykit';
import { ChevronDown, ChevronUp } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';
import usePersistedState from '@shared/hooks/usePersistedState';
import type { InternalMessage, TimelineVariant } from '../../MessagesV2.types';
import { FAIL_STATUSES, RAW_STATUS_CONFIG } from '../../MessagesV2.constants';
import { TimelineHistory } from './TimelineHistory';
import { OriginalMessageSubrow } from './OriginalMessageSubrow';
import { ResendCheckboxCell } from '../ResendSelection/ResendCheckboxCell';
import { ResendBadge } from '../ResendSelection/ResendBadge';
import { areAllResendableSelected, isResendRow, resendableOf } from '../ResendSelection/resendSelection.utils';
import { deriveProviderLabel, deriveStatusMessage } from '../../MessagesV2.utils';
import { getSmsPreviewUrl, getViewEmailLink } from './MessagesTable.utils';

// Track order, and the single place a column's width is declared:
// [select] Date Channel Provider Recipient "Message type" "Last status" "Status message"
// "Event ID" "Batch ID" View [expand]
// `headerLabels` and the row body below must stay in the same order, or header and body drift.
const GRID_WITH_SELECTION = '36px 132px 82px 84px 180px 190px 108px 200px 108px 118px 48px 40px';
const GRID_READ_ONLY = '132px 82px 84px 180px 190px 108px 200px 108px 118px 48px 40px';

/**
 * Derived, so a new column cannot leave the loading shimmer a column short. Split on runs of
 * whitespace, not a single space, so padding a track list for alignment cannot inflate the count.
 */
const READ_ONLY_COLUMN_COUNT = GRID_READ_ONLY.trim().split(/\s+/).length;

// External-link arrow for "view message" actions — no joykit ExternalLinkIcon exists.
const ExternalLinkSvg: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 6.5V10.5H1.5V2H5.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.5 1.5H10.5V4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.5 1.5L5.5 6.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Synchronous deep link to the message viewer — email viewer or SMS preview editor.
// Returns null when the source field is missing, so the action is hidden.
const getViewMessageUrl = (row: InternalMessage): string | null =>
  row.type === 'email' ? (getViewEmailLink(row.messageId ?? undefined) ?? null) : getSmsPreviewUrl(row.sentForEventId, row.id, row.createAt?.milliseconds);

type IdCellProps = Readonly<{ value: string | null | undefined; maxLen: number }>;

// Shows a truncated monospace ID. Hover for full value tooltip; double-click copies.
const IdCell: React.FC<IdCellProps> = ({ value, maxLen }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!value) return;
      const flash = (w: Window) => {
        setCopied(true);
        clearTimeout(timerRef.current);
        timerRef.current = w.setTimeout(() => setCopied(false), 1500);
      };
      withWindow(w => {
        if (w.navigator.clipboard) {
          w.navigator.clipboard.writeText(value).then(() => flash(w));
        } else {
          const el = w.document.createElement('textarea');
          el.value = value;
          w.document.body.appendChild(el);
          el.select();
          w.document.execCommand('copy');
          w.document.body.removeChild(el);
          flash(w);
        }
      });
    },
    [value],
  );

  if (!value) {
    return (
      <TextV2
        typographyVariant="label3"
        fontSize="12px"
        color="mono10"
      >
        –
      </TextV2>
    );
  }

  const truncated = value.length > maxLen ? value.slice(0, maxLen) + '…' : value;
  const needsTruncation = value.length > maxLen;

  const cell = (
    <TextV2
      typographyVariant="label3"
      fontSize="12px"
      fontFamily="monospace"
      cursor={needsTruncation ? 'help' : 'default'}
      onDoubleClick={handleDoubleClick}
      title={copied ? 'Copied!' : undefined}
    >
      {copied ? '✓ copied' : truncated}
    </TextV2>
  );

  if (!needsTruncation) return cell;

  return (
    <TooltipV2
      content={
        <TextV2
          typographyVariant="label3"
          fontFamily="monospace"
          style={{ wordBreak: 'break-all' }}
        >
          {value}
          <TextV2
            as="span"
            typographyVariant="label3"
            color="mono10"
            marginLeft="6px"
          >
            (double-click to copy)
          </TextV2>
        </TextV2>
      }
    >
      {cell}
    </TooltipV2>
  );
};

type LabeledIdCellProps = Readonly<{ label: string; value: string; maxLen?: number }>;

// A secondary id under a cell's main value, e.g. `person: 1a2b…`.
const LabeledIdCell: React.FC<LabeledIdCellProps> = ({ label, value, maxLen = 12 }) => (
  <Box
    display="flex"
    alignItems="center"
    gap="4px"
  >
    <TextV2
      typographyVariant="label3"
      fontSize="10px"
      color="mono10"
    >
      {label}
    </TextV2>
    <IdCell
      value={value}
      maxLen={maxLen}
    />
  </Box>
);

type BatchCellProps = Readonly<{ messageBatchId: string | null | undefined; stationeryOrderId: string | null | undefined }>;

// Each send of a card has its own batch and shares the card order, so a card order search lists the
// same order over many batch ids. Only card sends carry an order.
const BatchCell: React.FC<BatchCellProps> = ({ messageBatchId, stationeryOrderId }) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="2px"
      style={{ minWidth: 0 }}
    >
      <IdCell
        value={messageBatchId}
        maxLen={15}
      />
      {stationeryOrderId && (
        <LabeledIdCell
          label={tq`card:`}
          value={stationeryOrderId}
          // Narrower than the recipient column's ids: it shares the batch column's width.
          maxLen={8}
        />
      )}
    </Box>
  );
};

type ChannelBadgeProps = Readonly<{ type: string }>;

const ChannelBadge: React.FC<ChannelBadgeProps> = ({ type }) => {
  const isEmail = type === 'email';
  return (
    <Box
      display="inline-flex"
      alignItems="center"
      gap="4px"
      paddingX="8px"
      paddingY="2px"
      borderRadius="100px"
      fontSize="11px"
      fontWeight={600}
      style={{
        color: isEmail ? '#4951EF' : 'rgb(23,111,105)',
        background: isEmail ? 'rgb(238,235,255)' : 'rgb(232,255,248)',
      }}
    >
      {isEmail ? '✉' : '📱'} {isEmail ? 'Email' : 'SMS'}
    </Box>
  );
};

type StatusBadgeProps = Readonly<{ status: string }>;

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const cfg = RAW_STATUS_CONFIG[status] ?? { label: status, fg: '#767676', bg: '#F0F0F0' };
  return (
    <Box
      display="inline-block"
      paddingX="8px"
      paddingY="2px"
      borderRadius="100px"
      fontSize="11px"
      fontWeight={600}
      style={{ color: cfg.fg, background: cfg.bg }}
    >
      {cfg.label}
    </Box>
  );
};

const LoadingShimmer: React.FC = () => {
  const shimmerBlock = (
    <Box
      background="#EBEBEB"
      borderRadius={4}
      height="13px"
    />
  );
  return (
    <Box>
      {Array.from({ length: 7 }).map((_, ri) => (
        <Box
          key={ri}
          // The shimmer matches the read-only column set. It shows before any row exists, so the
          // selection column's presence is not yet meaningful.
          display="grid"
          gridTemplateColumns={GRID_READ_ONLY}
          gap="8px"
          padding="12px 16px"
          borderBottom="1px solid #F0F0F0"
        >
          {Array.from({ length: READ_ONLY_COLUMN_COUNT }).map((__, ci) => (
            <Box key={ci}>{shimmerBlock}</Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

type EmptyStateProps = Readonly<{ variant: 'pre-search' | 'zero' }>;

const EmptyState: React.FC<EmptyStateProps> = ({ variant }) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding="64px 32px"
      gap="12px"
    >
      {variant === 'pre-search' ? (
        <>
          <TextV2 typographyVariant="hed3">{tq`Search for messages`}</TextV2>
          <TextV2
            typographyVariant="body1"
            color="mono10"
            textAlign="center"
          >
            {tq`Enter a customer email, phone number, event ID, or batch ID to find messages`}
          </TextV2>
        </>
      ) : (
        <>
          <TextV2 typographyVariant="hed3">{tq`No messages found`}</TextV2>
          <TextV2
            typographyVariant="body1"
            color="mono10"
            textAlign="center"
          >
            {tq`Note: phone numbers must be in E.164 format — the tool auto-formats as you type`}
          </TextV2>
        </>
      )}
    </Box>
  );
};

type ErrorStateProps = Readonly<{ error: ApolloError }>;

const ErrorState: React.FC<ErrorStateProps> = ({ error }) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding="64px 32px"
      gap="12px"
    >
      <TextV2
        typographyVariant="hed3"
        color="error6"
      >
        {tq`Search failed`}
      </TextV2>
      <TextV2
        typographyVariant="body1"
        color="mono10"
        textAlign="center"
      >
        {error.message}
      </TextV2>
    </Box>
  );
};

type TimelineVariantToggleProps = Readonly<{
  value: TimelineVariant;
  onChange: (v: TimelineVariant) => void;
}>;

// Pill switcher: [≡ List] [⊞ Stepper]. Active tab: white bg + slight shadow; inactive: transparent.
const TimelineVariantToggle: React.FC<TimelineVariantToggleProps> = ({ value, onChange }) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');
  const options: ReadonlyArray<{ variant: TimelineVariant; glyph: string; label: string }> = [
    { variant: 'list', glyph: '≡', label: tq`List` },
    { variant: 'stepper', glyph: '⊞', label: tq`Stepper` },
  ];
  return (
    <Box
      display="inline-flex"
      padding="2px"
      borderRadius="100px"
      background="#EBEBF2"
    >
      {options.map(opt => {
        const active = value === opt.variant;
        return (
          <Box
            as="button"
            key={opt.variant}
            type="button"
            display="inline-flex"
            alignItems="center"
            gap="5px"
            paddingX="10px"
            paddingY="3px"
            borderRadius="100px"
            border="none"
            fontSize="11px"
            fontWeight={600}
            cursor="pointer"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onChange(opt.variant);
            }}
            style={{
              background: active ? '#FFFFFF' : 'transparent',
              color: active ? '#22223B' : '#767676',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
            }}
          >
            {opt.glyph} {opt.label}
          </Box>
        );
      })}
    </Box>
  );
};

type MessagesTableProps = Readonly<{
  /** Already folded to one row per message — see `foldToLatestAttempt`. */
  messages: InternalMessage[];
  /** Which attempt each row is, counting the first send as 1 — see `deriveAttemptNumbers`. */
  attemptNumberById: ReadonlyMap<string, number>;
  loading: boolean;
  called: boolean;
  error?: ApolloError;
  /**
   * Whether the viewer may resend. This route is NOT behind the service center's super-admin
   * gate — that only guards the hub page — so a non-super-admin can reach it and must not be
   * shown controls the backend will reject.
   */
  canResend: boolean;
  selectedIds: ReadonlySet<string>;
  onToggleSelected: (id: string) => void;
  onToggleSelectAll: (rows: readonly InternalMessage[]) => void;
}>;

export const MessagesTable: React.FC<MessagesTableProps> = ({
  messages,
  attemptNumberById,
  loading,
  called,
  error,
  canResend,
  selectedIds,
  onToggleSelected,
  onToggleSelectAll,
}) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');
  // Last entry is the expand/collapse column, intentionally unlabeled.
  // First entry is the select-all checkbox cell, rendered separately below; last is the
  // expand/collapse column, intentionally unlabeled.
  const headerLabels = [tq`Date`, tq`Channel`, tq`Provider`, tq`Recipient`, tq`Message type`, tq`Last status`, tq`Status message`, tq`Event ID`, tq`Batch ID`, tq`View`, ''];
  const GRID = canResend ? GRID_WITH_SELECTION : GRID_READ_ONLY;
  const allResendableSelected = areAllResendableSelected(messages, selectedIds);
  const hasResendableRows = resendableOf(messages).length > 0;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [tlVariant, setTlVariant] = usePersistedState<TimelineVariant>('servicecenter:messagesv2:timelineVariant', 'list');

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (loading) return <LoadingShimmer />;
  if (!called) return <EmptyState variant="pre-search" />;
  if (error) return <ErrorState error={error} />;
  if (messages.length === 0) return <EmptyState variant="zero" />;

  return (
    <Box
      overflowX="auto"
      minWidth={0}
      border="1px solid #E8E8E8"
      borderRadius={8}
    >
      {/* Header */}
      <Box
        display="grid"
        gridTemplateColumns={GRID}
        gap="8px"
        padding="8px 16px"
        background="#F7F8FC"
        borderBottom="1px solid #E8E8E8"
        borderLeft="3px solid transparent"
      >
        {canResend && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CheckboxV2
              isChecked={allResendableSelected}
              isDisabled={!hasResendableRows}
              onChange={() => onToggleSelectAll(messages)}
              aria-label={tq`Select all email messages on this page`}
            />
          </Box>
        )}
        {headerLabels.map((label, i) => (
          <TextV2
            key={i}
            typographyVariant="label3"
            color="mono10"
            fontWeight={600}
          >
            {label}
          </TextV2>
        ))}
      </Box>

      {/* Rows */}
      {messages.map(row => {
        const isFail = FAIL_STATUSES.has(row.rawStatus);
        const isExpanded = expandedIds.has(row.id);
        const dateMs = row.createAt.milliseconds;
        const dateStr = new Date(dateMs).toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short',
        });
        const recipientPrimary = row.recipient.email || row.recipient.phoneNumber || '–';
        const statusMsg = deriveStatusMessage(row);
        const providerLabel = deriveProviderLabel(row);
        const viewUrl = getViewMessageUrl(row);

        return (
          <React.Fragment key={row.id}>
            <Box
              display="grid"
              gridTemplateColumns={GRID}
              gap="8px"
              padding="10px 16px"
              alignItems="center"
              borderBottom="1px solid #F0F0F0"
              cursor="pointer"
              style={{
                borderLeft: isFail ? '3px solid rgb(220,38,38)' : '3px solid transparent',
                background: isFail ? 'rgba(220,38,38,0.03)' : undefined,
              }}
              _hover={{ background: '#FAFAFA' }}
              onClick={() => toggleExpand(row.id)}
            >
              {canResend && (
                <ResendCheckboxCell
                  message={row}
                  isSelected={selectedIds.has(row.id)}
                  onToggle={onToggleSelected}
                />
              )}
              <TextV2
                typographyVariant="label3"
                fontSize="11px"
              >
                {dateStr}
              </TextV2>
              <Box>
                <ChannelBadge type={row.type} />
              </Box>
              {/* `title` because the track is sized for the providers we send through today; an
                  unfamiliar longer name must still be readable rather than clipped to nothing. */}
              <TextV2
                typographyVariant="label3"
                fontSize="11px"
                color={providerLabel ? 'mono14' : 'mono10'}
                title={providerLabel || undefined}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {providerLabel || '–'}
              </TextV2>
              <Box
                display="flex"
                flexDirection="column"
                gap="2px"
                style={{ minWidth: 0 }}
              >
                <TextV2
                  typographyVariant="label3"
                  fontSize="12px"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {recipientPrimary}
                </TextV2>
                {row.recipient.personId && (
                  <LabeledIdCell
                    label={tq`person:`}
                    value={row.recipient.personId}
                  />
                )}
                {row.recipient.guestKey && (
                  <LabeledIdCell
                    label={tq`guest:`}
                    value={row.recipient.guestKey}
                  />
                )}
                {row.fromEmail && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="4px"
                    style={{ minWidth: 0 }}
                  >
                    <TextV2
                      typographyVariant="label3"
                      fontSize="10px"
                      color="mono10"
                    >
                      from:
                    </TextV2>
                    <TextV2
                      typographyVariant="label3"
                      fontSize="10px"
                      color="mono10"
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {row.fromEmail}
                    </TextV2>
                  </Box>
                )}
                {row.replyToEmail && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="4px"
                    style={{ minWidth: 0 }}
                  >
                    <TextV2
                      typographyVariant="label3"
                      fontSize="10px"
                      color="mono10"
                    >
                      reply-to:
                    </TextV2>
                    <TextV2
                      typographyVariant="label3"
                      fontSize="10px"
                      color="mono10"
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {row.replyToEmail}
                    </TextV2>
                  </Box>
                )}
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                gap="3px"
                style={{ minWidth: 0 }}
              >
                <TextV2
                  typographyVariant="label3"
                  fontSize="12px"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {row.messageType?.replace(/_/g, ' ') || '–'}
                </TextV2>
                {/* Sits with the message type because that column already says what kind of
                    message this is; a later attempt is a kind. Expand the row to see the original.
                    The map covers every rendered row, so the fallback is unreachable — and 2 is the
                    floor for any resend, so it can only under-count, never claim a first send. */}
                {isResendRow(row) && (
                  <Box>
                    <ResendBadge attemptNumber={attemptNumberById.get(row.id) ?? 2} />
                  </Box>
                )}
              </Box>
              <Box>
                <StatusBadge status={row.rawStatus} />
              </Box>
              <TextV2
                typographyVariant="label3"
                fontSize="12px"
                fontFamily={statusMsg ? 'monospace' : undefined}
                color={statusMsg ? 'mono14' : 'mono10'}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {statusMsg || '–'}
              </TextV2>
              <IdCell
                value={row.sentForEventId}
                maxLen={13}
              />
              <BatchCell
                messageBatchId={row.messageBatchId}
                stationeryOrderId={row.stationeryOrderId}
              />
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {viewUrl && (
                  <Box
                    as="button"
                    type="button"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      withWindow(w => w.open(viewUrl, '_blank'));
                    }}
                    aria-label={row.type === 'email' ? tq`View email` : tq`View SMS`}
                    color="mono10"
                    _hover={{ color: 'mono14' }}
                    padding={0}
                    display="flex"
                    alignItems="center"
                    __css={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <ExternalLinkSvg />
                  </Box>
                )}
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {isExpanded ? <ChevronUp size="sm" /> : <ChevronDown size="sm" />}
              </Box>
            </Box>

            {isExpanded && (
              <Box
                background="#F7F8FC"
                padding="14px 20px 18px 36px"
                borderBottom="1px solid #EBEBEB"
                style={{ borderLeft: `3px solid ${isFail ? 'rgb(220,38,38)' : '#4951EF'}` }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  marginBottom="14px"
                  flexWrap="wrap"
                  gap="8px"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="14px"
                    flexWrap="wrap"
                  >
                    <TextV2
                      fontSize="12.5px"
                      fontWeight={700}
                    >
                      {tq`Status history`}
                    </TextV2>
                    <TextV2
                      fontSize="11px"
                      color="mono10"
                      fontFamily="monospace"
                    >
                      {row.recipient.email || row.recipient.phoneNumber}
                    </TextV2>
                  </Box>
                  <TimelineVariantToggle
                    value={tlVariant}
                    onChange={setTlVariant}
                  />
                </Box>
                <TimelineHistory
                  entries={row.rawStatusHistory ?? []}
                  variant={tlVariant}
                />
                {/* Only resend rows have an original to show. Fetched on expand, not with the list. */}
                {row.originalMessageId && <OriginalMessageSubrow originalMessageId={row.originalMessageId} />}
                {viewUrl && (
                  <Box
                    marginTop={14}
                    paddingTop={12}
                    borderTop="1px solid #E5E7EB"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    <ButtonV2
                      variant="link"
                      onClick={() => withWindow(w => w.open(viewUrl, '_blank'))}
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <ExternalLinkSvg />
                      {row.type === 'email' ? tq`View email in viewer` : tq`View SMS preview`}
                    </ButtonV2>
                  </Box>
                )}
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};
